/**
 * SubagentsSidebar — slide-in right panel showing detailed subagent
 * activity for the currently selected step.
 *
 * Data model: consumes ``ToolActivity[]`` (same shape the supervisor
 * emits while streaming). Shows one row per top-level tool call; each
 * row expands to show the specialist's nested timeline (reasoning +
 * inner tool calls with input/output JSON). Expanded state is keyed by
 * tool-call id so the caller can pre-open a row by passing
 * ``selectedId``.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  friendlyToolName,
  summarizeToolInput,
  summarizeToolOutput,
} from './tool-display';

/** Pretty-print a tool's input/output payload for the ``<details>`` panels.
 *  Tolerates non-JSON-serialisable values (errors, class instances) by
 *  falling back to ``String()``. */
function formatJson(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// Mirror the ToolActivity / SubActivity shapes from RavenChat.tsx without
// cross-file imports (keeps the sidebar independently compilable).
export interface SidebarSubActivity {
  id: string;
  tool: string;
  input?: unknown;
  output?: unknown;
  status: 'running' | 'done';
}

export type SidebarToolActivityItem =
  | { kind: 'reasoning'; text: string }
  | { kind: 'subcall'; sub: SidebarSubActivity };

export interface SidebarToolActivity {
  id: string;
  agent: string;
  status: 'running' | 'done';
  items?: SidebarToolActivityItem[];
}


interface SubagentsSidebarProps {
  open: boolean;
  onClose: () => void;
  /** Tool activities from the message currently being inspected. */
  tools: SidebarToolActivity[];
  /** Tool-call id to pre-expand + scroll into view. */
  selectedId?: string | null;
}

export function SubagentsSidebar({
  open, onClose, tools, selectedId,
}: SubagentsSidebarProps) {
  // Per-id expanded state. Selecting from the chat auto-expands that row.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // When ``selectedId`` changes, ensure it's expanded + scroll into view.
  useEffect(() => {
    if (!open || !selectedId) return;
    setExpanded(prev => {
      if (prev.has(selectedId)) return prev;
      const next = new Set(prev);
      next.add(selectedId);
      return next;
    });
    // Scroll after DOM updates.
    requestAnimationFrame(() => {
      const el = rowRefs.current.get(selectedId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [open, selectedId]);

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toolCount = tools.length;
  const subcallCount = useMemo(
    () => tools.reduce((n, t) => n + (t.items?.filter(i => i.kind === 'subcall').length ?? 0), 0),
    [tools],
  );

  return (
    <aside
      className={`rv-sidebar ${open ? 'rv-sidebar--open' : ''}`}
      aria-hidden={!open}
    >
      <header className="rv-sidebar__header">
        <div className="rv-sidebar__title">Trace</div>
        <div className="rv-sidebar__counts" aria-hidden="true">
          {toolCount} {toolCount === 1 ? 'call' : 'calls'}
          {subcallCount > 0 && <> · {subcallCount} nested</>}
        </div>
        <button
          type="button"
          className="rv-sidebar__close"
          onClick={onClose}
          aria-label="Close sidebar"
        >
          ×
        </button>
      </header>

      <div className="rv-sidebar__body">
        {tools.length === 0 && (
          <div className="rv-sidebar__empty">No subagent activity yet.</div>
        )}
        {tools.filter(t => !t.agent.endsWith('_start') && !t.agent.endsWith('_end')).map(t => {
          const isExpanded = expanded.has(t.id);
          const isSelected = selectedId === t.id;
          return (
            <div
              key={t.id || t.agent}
              ref={(el) => {
                if (el) rowRefs.current.set(t.id, el);
                else rowRefs.current.delete(t.id);
              }}
              className={`rv-subagent ${isSelected ? 'rv-subagent--selected' : ''}`}
            >
              <button
                type="button"
                className="rv-subagent__head"
                onClick={() => toggle(t.id)}
                aria-expanded={isExpanded}
              >
                <span
                  className={`rv-subagent__status rv-subagent__status--${t.status}`}
                  aria-hidden="true"
                >
                  {t.id === 'node_evidence_verifier'
                    ? (t.status === 'running' ? '🔍' : '✓')
                    : (t.status === 'running' ? '●' : '✓')}
                </span>
                <span className="rv-subagent__agent">
                  {t.id === 'node_evidence_verifier' ? 'Verification' : t.agent}
                </span>
                <span className="rv-subagent__caret" aria-hidden="true">
                  {isExpanded ? '▾' : '▸'}
                </span>
              </button>
              {isExpanded && (
                <div className="rv-subagent__body">
                  {(!t.items || t.items.length === 0) && (
                    <div className="rv-subagent__empty">
                      {t.status === 'running'
                        ? 'Specialist is running — details will appear as it emits events.'
                        : 'No nested activity recorded.'}
                    </div>
                  )}
                  {t.items?.map((item, idx) => {
                    if (item.kind === 'reasoning') {
                      return (
                        <div key={`r-${idx}`} className="rv-subagent__reasoning">
                          <span aria-hidden="true">🤖</span> {item.text}
                        </div>
                      );
                    }
                    const s = item.sub;
                    const friendly = friendlyToolName(s.tool);
                    const inputSummary = summarizeToolInput(s.tool, s.input);
                    const outputSummary =
                      s.status === 'done' ? summarizeToolOutput(s.tool, s.output) : '';
                    return (
                      <div key={`s-${s.id}`} className={`rv-subcall-row rv-subcall-row--${s.status}`}>
                        <div className="rv-subcall-row__head">
                          <span className="rv-subcall-row__icon" aria-hidden="true">
                            {s.status === 'running' ? '○' : '✓'}
                          </span>
                          <span className="rv-subcall-row__tool" title={s.tool}>{friendly}</span>
                          {inputSummary && (
                            <span className="rv-subcall-row__summary">{inputSummary}</span>
                          )}
                        </div>
                        {outputSummary && (
                          <div className="rv-subcall-row__output">→ {outputSummary}</div>
                        )}
                        {(s.input !== undefined || (s.output !== undefined && s.status === 'done')) && (
                          <div className="rv-subcall-row__panels">
                            {s.input !== undefined && (
                              <details className="rv-subcall-row__details">
                                <summary>input</summary>
                                <pre>{formatJson(s.input)}</pre>
                              </details>
                            )}
                            {s.output !== undefined && s.status === 'done' && (
                              <details className="rv-subcall-row__details">
                                <summary>output</summary>
                                <pre>{formatJson(s.output)}</pre>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
